import { useAdmCapabilities } from "../admCountryCapabilities";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardTitle } from "campaign-builder/ui";
import type { BuilderStepProps } from "campaign-builder/config";
import type { AdmBuilderSnapshot } from "campaign-builder/channels/directmail/adm/adm.config";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useDesignSelection } from "../designSelection";
import { useTargetingValidation } from "../targetingValidation";
import { useTrackingValidity } from "../trackingState";
import { useDesignPlaceholders } from "./placeholders";
import { useTrackingConfig, type TrackingConfigState, type VariableFileOption } from "./useTrackingConfig";
import { parseVariableCsv, uploadVariableFile } from "./uploadVariableFile";
import { PlaceholdersCard } from "./PlaceholdersCard";
import { TrackingConfigCard } from "./TrackingConfigCard";

export const AdmTrackingStep: React.FC<BuilderStepProps> = ({ campaign }) => {
  const {
    campaignId,
    clientId,
    campaignData,
    variables,
    campaignVariables,
    recipientsAmount,
    refetchVariables,
    refetchCampaignVariables,
  } = useCampaignData();
  // Outside the US there is one code per campaign; OPS uploads per-recipient codes in Internal Files.
  const isIntl = !useAdmCapabilities().allowsPerRecipientCodes;
  const { selectedDesign } = useDesignSelection();
  const { setTrackingValid } = useTrackingValidity();
  const { version: validationVersion } = useTargetingValidation();

  // The campaign has a design iff it carries an assigned design id. Read it from
  // the shell snapshot (the parent already derived it) rather than fetching flyer
  // info a second time here.
  const hasDesign = !!(campaign as AdmBuilderSnapshot).hasDesign;
  const designHydrating = hasDesign && !selectedDesign;

  const { placeholders, loading } = useDesignPlaceholders(selectedDesign, clientId);

  const variableFiles: VariableFileOption[] = useMemo(
    () =>
      (variables ?? [])
        .filter((variable) => !variable.deleted_at)
        .map((variable) => ({
          id: String(variable.id),
          name: variable.name,
          columns: variable.csvColumns || variable.csv_columns || [],
          rowsAmount: variable.metadata?.rowsAmount ?? 0,
          metadata: variable.metadata,
        })),
    [variables]
  );

  const tracking = useTrackingConfig({
    campaignId,
    campaignData,
    campaignVariables,
    variableFiles,
    placeholders,
    refetchCampaignVariables,
  });

  const interacted = useRef(false);
  const update = useCallback(
    (patch: Partial<TrackingConfigState>) => {
      interacted.current = true;
      tracking.update(patch);
    },
    [tracking]
  );

  useEffect(() => {
    setTrackingValid(tracking.isValid);
  }, [tracking.isValid, setTrackingValid]);

  // Intl campaigns can't reach the "unique" mode through the UI, but a
  // campaign persisted before the country switch could hydrate with it —
  // normalize so the hidden ModeCards never strand the step in an invalid state.
  useEffect(() => {
    if (!isIntl) return;
    const patch: Partial<TrackingConfigState> = {};
    if (tracking.state.promoEnabled && tracking.state.promoMode !== "same") patch.promoMode = "same";
    if (tracking.state.qrEnabled && tracking.state.qrMode !== "same") patch.qrMode = "same";
    if (Object.keys(patch).length) tracking.update(patch);
  }, [isIntl, tracking.state.promoEnabled, tracking.state.promoMode, tracking.state.qrEnabled, tracking.state.qrMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (interacted.current) void tracking.persistCoupon();
  }, [tracking.state.promoEnabled, tracking.state.promoMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (interacted.current) void tracking.persistQr();
  }, [tracking.state.qrEnabled, tracking.state.qrMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const [showErrors, setShowErrors] = useState(false);
  const lastValidation = useRef(0);
  useEffect(() => {
    if (validationVersion === lastValidation.current) return;
    lastValidation.current = validationVersion;
    if (!tracking.isValid) {
      setShowErrors(true);
      requestAnimationFrame(() => {
        document
          .querySelector('[data-targeting-section="tracking"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [validationVersion, tracking.isValid]);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleUploadFile = useCallback(
    async (file: File) => {
      if (!clientId) return;
      setUploadError(null);
      setUploading(true);
      try {
        const parsed = await parseVariableCsv(file);
        if (parsed.totalRows < recipientsAmount) {
          setUploadError(
            `This file has ${parsed.totalRows} row${
              parsed.totalRows === 1 ? "" : "s"
            }, fewer than your audience size (${recipientsAmount}). Upload a file with at least ${recipientsAmount} rows.`
          );
          return;
        }
        const created = await uploadVariableFile({ clientId, file, parsed });
        refetchVariables();
        await tracking.selectFile(created.id, created.columns);
      } catch (err) {
        console.error("variable upload error", err);
        setUploadError("There was an error while uploading the file. Please try again.");
      } finally {
        setUploading(false);
      }
    },
    [clientId, recipientsAmount, refetchVariables, tracking]
  );

  const fileError =
    uploadError ??
    (showErrors && tracking.hasUnmappedPlaceholders ? "Map every placeholder to a column in the file." : null);

  if (!hasDesign) {
    return (
      <Card className="flex flex-col gap-2 p-[25px]" data-copilot-step="ADM Tracking">
        <CardTitle>Tracking</CardTitle>
        <p className="text-muted-foreground text-sm leading-5">
          Select a design first — placeholders are detected from your design.
        </p>
      </Card>
    );
  }

  return (
    <>
      {!isIntl && (
        <PlaceholdersCard
          placeholders={placeholders}
          loading={loading || designHydrating}
          variableFiles={variableFiles}
          recipientsAmount={recipientsAmount}
          selectedFileId={tracking.state.variableFileId}
          columnMapping={tracking.state.columnMapping}
          fileError={fileError}
          uploading={uploading}
          onSelectFile={tracking.selectFile}
          onMapColumn={tracking.mapColumn}
          onUploadFile={handleUploadFile}
        />
      )}
      <TrackingConfigCard
        state={tracking.state}
        placeholders={isIntl ? [] : placeholders}
        showErrors={showErrors}
        update={update}
        onPersistCoupon={tracking.persistCoupon}
        onPersistQr={tracking.persistQr}
        singleCodeOnly={isIntl}
      />
    </>
  );
};
